import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';

export default function Register() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [vendorSlug, setVendorSlug] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const { login } = useAuth();

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Auto-format the slug to lowercase with hyphens
        const formattedSlug = vendorSlug.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');

        try {
            const res = await fetch(`${config.backendOrigin}/api/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password, name, vendor_slug: formattedSlug })
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.error || 'Failed to register');
            }

            login(data.token, data.user);
            navigate('/dashboard');
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto mt-20 p-8 bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl animate-in slide-in-from-bottom-4 duration-500">
            <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-purple-400 mb-6 text-center">
                Create Store
            </h2>

            {error && (
                <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6 text-sm">
                    {error}
                </div>
            )}

            <form onSubmit={handleRegister} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-neutral-400 mb-1">Email</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-neutral-400 mb-1">Password</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                        required
                        minLength={6}
                    />
                </div>

                <div className="pt-2 border-t border-neutral-800 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-neutral-400 mb-1">Display Name (Optional)</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Ash Ketchum"
                            className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-neutral-400 mb-1">Public URL Slug</label>
                        <div className="flex border border-neutral-800 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-pink-500 focus-within:border-transparent transition-all">
                            <span className="bg-neutral-950 text-neutral-500 px-3 py-3 font-mono text-sm border-r border-neutral-800 select-none">
                                tcgbinder.com/vendor/
                            </span>
                            <input
                                type="text"
                                value={vendorSlug}
                                onChange={(e) => setVendorSlug(e.target.value)}
                                placeholder="my-store"
                                className="w-full bg-neutral-950 px-3 py-3 text-white focus:outline-none font-mono text-sm"
                                required
                            />
                        </div>
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white font-bold py-3 px-4 rounded-lg shadow-lg shadow-pink-500/25 transition-all outline-none focus:ring-2 focus:ring-white disabled:opacity-50 mt-6"
                >
                    {loading ? 'Creating...' : 'Launch Store'}
                </button>
            </form>

            <p className="mt-6 text-center text-sm text-neutral-500">
                Already have an account?{' '}
                <Link to="/login" className="text-pink-400 hover:text-pink-300 transition-colors">
                    Sign in
                </Link>
            </p>
        </div>
    );
}
